import { useMemo } from 'react';

import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { useClientController_Overview } from '@/services/hooks';
import type { ClientOverviewResponseDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import { mapClientOverviewToDomain } from './mappers';
import type { UseGetClientOverviewResult } from './types';

function useClientOverviewQuery(
    clientId: number,
    includeFishGroups: boolean,
    options?: DomainQueryOptions<ClientOverviewResponseDto>
): UseGetClientOverviewResult {
    const query = useClientController_Overview(
        clientId,
        // Sent only when true: an explicit `false` would be one more cache key for
        // a response identical to the default.
        includeFishGroups ? { includeFishGroups: true } : undefined,
        options
    );

    const clientOverview = useMemo(() => {
        if (!query.data?.data) return null;
        return mapClientOverviewToDomain(query.data.data);
    }, [query.data?.data]);

    return {
        data: clientOverview,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
        refetch: query.refetch,
    };
}

/**
 * The client overview, with cohort information when `fish-group-tracking` is on.
 *
 * The flag is read **here** rather than taken as a parameter. Whether to ask for
 * fish groups is not a per-caller decision — it is the same answer for every
 * consumer of this endpoint, and `LocationOverviewTable` already reads the same
 * flag to decide whether to render the column. Threading it through the six call
 * sites only created six chances for the request and the rendering to disagree,
 * and left four callers passing a parameter they had no opinion about.
 *
 * While PostHog is still resolving flags, `enabled` is false, so the first
 * request is the plain one. A flagged user then gets one refetch when the flag
 * lands (the query key contains the params); an unflagged user — the common case
 * — never refetches, because the value goes from false to false.
 */
export const useGetClientOverview = (
    clientId: number,
    options?: DomainQueryOptions<ClientOverviewResponseDto>
): UseGetClientOverviewResult => {
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');
    return useClientOverviewQuery(clientId, fishGroupsEnabled, options);
};

/**
 * The client overview, always with cohort information, for the fish group board. The board
 * sits behind its own route flag and cannot work without the cohort fields: `emptyCages` are
 * the pens its create dialogs offer, and `closedFishGroups` keep Suggest from reusing a
 * closed group's name.
 */
export const useGetClientOverviewWithFishGroups = (
    clientId: number,
    options?: DomainQueryOptions<ClientOverviewResponseDto>
): UseGetClientOverviewResult => useClientOverviewQuery(clientId, true, options);
